import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { updateCampaignVariant } from 'actions/campaignVariants'
import Formsy from 'formsy-react'
import { buildTranslate } from 'locales'
import { VariantOrCampaign } from 'models/CampaignVariant'
import React, { useEffect, useRef, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { RouteComponentProps } from 'react-router-dom'
import { push } from 'react-router-redux'
import PushForm from 'scenes/CampaignCenter/Builder/components/Forms/Push'
import PushPreview from 'scenes/CampaignCenter/Builder/components/Previews/Push'
import CampaignConfigContext from '../Builder/CampaignConfigContext'
import EditTopBar from './EditTopBar'
import { useEditCampaign } from './hooks'

const t = buildTranslate(`thanx_campaigns.builder.steps.setup.messaging`)

type FormModel = {
  notify_push_text: string
  notify_push_title: string
  notify_push_subtitle: string
  notify_push_image?: string
}

type Props = RouteComponentProps<{
  id: string
  type: string
  variantId: string
}>

const EditPush: React.FC<Props> = ({ match }) => {
  const [css] = useStyletron()
  const [isValid, setIsValid] = useState<boolean>(true)
  const campaignId = Number(match.params.id)
  const variantId = Number(match.params.variantId)
  const dispatch = useDispatch()

  const { campaign, variant, config, isSaving, debouncedSave, triggerSave } =
    useEditCampaign(campaignId, variantId)

  const [pushText, setPushText] = useState(variant?.notify_push_text)
  const [pushTitle, setPushTitle] = useState(variant?.notify_push_title)
  const [pushSubtitle, setPushSubtitle] = useState(
    variant?.notify_push_subtitle
  )
  // Live data-URL of a just-picked image (write-only field, never read back),
  // so the device preview updates immediately on upload. Falls back to the
  // persisted hero image URL from `config` when re-editing a saved campaign.
  const [pushImage, setPushImage] = useState<string | undefined>(undefined)

  // Reset the live-upload preview when switching A/B variants. `pushImage` is
  // set-only (it wins the `||` over the persisted hero URL), and changing
  // `:variantId` re-renders this component without remounting it — so without
  // this reset, variant B would keep showing the image just uploaded on
  // variant A until its own upload.
  useEffect(() => {
    setPushImage(undefined)
  }, [variantId])

  const form = useRef<Formsy | null>(null)

  function onContinue() {
    if (!isValid) {
      form?.current?.setFormPristine?.(false)
      return
    }
    if (form.current) {
      triggerSave(form.current.getModel())
    }
    dispatch(push(`/thanx_campaigns/targeted/custom/${campaignId}`))
  }

  async function onRemove() {
    await dispatch(
      updateCampaignVariant(campaignId, variantId, {
        ...config,
        notify_push: false,
      })
    )
    dispatch(push(`/thanx_campaigns/targeted/custom/${campaignId}`))
  }

  async function onFormChange(model: FormModel) {
    setPushText(model.notify_push_text)
    setPushTitle(model.notify_push_title)
    setPushSubtitle(model.notify_push_subtitle)
    // Track the field whenever it is present, including '' — the merchant
    // clicking the trash icon clears it to '', which must drive the preview
    // back to empty. Only skip when the field is absent entirely (undefined),
    // so unrelated form changes don't clobber a live upload.
    if (model.notify_push_image !== undefined) {
      setPushImage(model.notify_push_image)
    }
    if (!isValid) return
    debouncedSave(model)
  }

  if (!campaign || !variant || !config) return null

  return (
    <div
      className={`${css({
        marginTop: '30px',
        minHeight: `calc(100% - 30px)`,
      })}`}
    >
      <CampaignConfigContext.Provider
        value={{
          config: config as unknown as VariantOrCampaign,
          setConfig: () => {},
        }}
      >
        <EditTopBar
          messageType="push"
          campaign={campaign}
          variant={variant}
          isSaving={isSaving}
          onContinue={onContinue}
          onRemove={onRemove}
        />
        <div
          className={`d-flex align-center ${css({
            height: '64px',
            borderBottom: '1px solid #ddd',
            paddingLeft: '24px',
          })}`}
        >
          <Text.Header4 className={css({ lineHeight: '2' })}>
            {t('push.title')}
          </Text.Header4>
        </div>
        <Formsy
          onChange={onFormChange}
          ref={form}
          onValid={() => setIsValid(true)}
          onInvalid={() => setIsValid(false)}
        >
          <Row>
            <Col
              md={6}
              className="padding-top-huge full-height-without-navs overflow-y"
            >
              <div className="display-flex flex-justify-center">
                <PushPreview
                  text={pushText ?? null}
                  title={pushTitle ?? null}
                  subtitle={pushSubtitle ?? null}
                  imageUrl={
                    pushImage ?? config?.notify_push_hero_image_urls?.default
                  }
                />
              </div>
            </Col>
            <Col
              md={6}
              className={`
                grey-10-bkg padding-top-huge
                padding-left-extra-large padding-right-extra-large
                full-height-without-navs overflow-y
              `}
            >
              <PushForm campaign={campaign} />
            </Col>
          </Row>
        </Formsy>
      </CampaignConfigContext.Provider>
    </div>
  )
}

export default EditPush
